/**
 * @file 前端旧版本失效监控
 * @description 轮询部署版本清单，并以可恢复退避方式替换旧页面。
 */

/** 服务端版本清单结构。 */
export interface BuildVersionManifest {
  /** 已部署前端的唯一构建标识。 */
  buildId: string
}

/** 旧页面刷新重试记录。 */
export interface ReleaseRetryRecord {
  /** 发起刷新时页面内嵌的构建标识。 */
  sourceBuildId: string
  /** 期望加载的远端构建标识。 */
  targetBuildId: string
  /** 当前版本组合已经发起的刷新次数。 */
  attempt: number
  /** 下次允许刷新页面的时间戳。 */
  nextRetryAt: number
}

/** 单次旧版本失效检查所需运行时。 */
export interface ReleaseInvalidationRuntime {
  /** 当前页面内嵌的构建标识。 */
  currentBuildId: string
  /** 获取服务端最新构建标识。 */
  fetchBuildId: () => Promise<string | null>
  /** 读取当前版本组合的刷新重试记录。 */
  readRetryRecord: () => ReleaseRetryRecord | null
  /** 写入当前版本组合的刷新重试记录。 */
  writeRetryRecord: (record: ReleaseRetryRecord) => void
  /** 清除已经完成或失效的刷新重试记录。 */
  clearRetryRecord: () => void
  /** 创建保留当前路由的防缓存替换地址。 */
  createReloadUrl: (record: ReleaseRetryRecord, requestedAt: number) => string
  /** 使用指定地址替换当前页面。 */
  replace: (url: string) => void
  /** 获取当前时间戳。 */
  now: () => number
  /** 首次刷新失败后的基础退避时长。 */
  retryBaseDelayMs: number
  /** 连续刷新失败后的最大退避时长。 */
  retryMaxDelayMs: number
}

/** 版本失效监控配置。 */
export interface ReleaseInvalidationOptions {
  /** 版本清单轮询间隔，单位毫秒。 */
  intervalMs?: number
  /** 当前页面构建标识，默认使用编译期常量。 */
  currentBuildId?: string
  /** 首次刷新失败后的基础退避时长，单位毫秒。 */
  retryBaseDelayMs?: number
  /** 连续刷新失败后的最大退避时长，单位毫秒。 */
  retryMaxDelayMs?: number
}

/** 默认版本清单轮询间隔。 */
const DEFAULT_RELEASE_CHECK_INTERVAL_MS = 60_000

/** 默认刷新重试基础间隔。 */
const DEFAULT_RELEASE_RETRY_BASE_DELAY_MS = 60_000

/** 默认刷新重试最大间隔。 */
const DEFAULT_RELEASE_RETRY_MAX_DELAY_MS = 5 * 60_000

/** 部署版本清单地址。 */
const VERSION_MANIFEST_PATH = '/version.json'

/** 刷新重试记录的会话存储键。 */
const RELEASE_RETRY_STORAGE_KEY = 'tdm:release-invalidation:retry'

/** 防缓存地址中的来源构建参数。 */
const RELEASE_SOURCE_QUERY_KEY = '__tdm_release_from'

/** 防缓存地址中的目标构建参数。 */
const RELEASE_TARGET_QUERY_KEY = '__tdm_release_to'

/** 防缓存地址中的刷新次数参数。 */
const RELEASE_ATTEMPT_QUERY_KEY = '__tdm_release_attempt'

/** 防缓存地址中的下次重试时间参数。 */
const RELEASE_RETRY_AT_QUERY_KEY = '__tdm_release_retry_at'

/** 防缓存地址中的本次请求时间参数。 */
const RELEASE_NONCE_QUERY_KEY = '__tdm_release_nonce'

/** 所有发布失效专用查询参数。 */
const RELEASE_QUERY_KEYS = [
  RELEASE_SOURCE_QUERY_KEY,
  RELEASE_TARGET_QUERY_KEY,
  RELEASE_ATTEMPT_QUERY_KEY,
  RELEASE_RETRY_AT_QUERY_KEY,
  RELEASE_NONCE_QUERY_KEY
] as const

/**
 * 规范化构建标识。
 * @param buildId 待处理的构建标识。
 * @returns 去除首尾空白后的构建标识。
 */
const normalizeBuildId = (buildId: string): string => buildId.trim()

/**
 * 规范化正整数配置。
 * @param value 用户配置值。
 * @param fallback 非法配置的回退值。
 * @returns 可用于计时器的正整数。
 */
const normalizePositiveInteger = (
  value: number | undefined,
  fallback: number
): number =>
  Number.isFinite(value) && Number(value) > 0
    ? Math.floor(Number(value))
    : fallback

/**
 * 解析服务端版本清单。
 * @param value 服务端返回的未知 JSON 值。
 * @returns 合法构建标识；结构无效时返回 null。
 */
export const parseBuildVersionManifest = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null
  const buildId = (value as Partial<BuildVersionManifest>).buildId
  if (typeof buildId !== 'string') return null
  return normalizeBuildId(buildId) || null
}

/**
 * 解析刷新重试记录。
 * @param value 会话存储或地址参数还原出的未知值。
 * @returns 合法刷新记录；结构无效时返回 null。
 */
export const parseReleaseRetryRecord = (
  value: unknown
): ReleaseRetryRecord | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (
    typeof record.sourceBuildId !== 'string' ||
    typeof record.targetBuildId !== 'string'
  ) {
    return null
  }
  const sourceBuildId = normalizeBuildId(record.sourceBuildId)
  const targetBuildId = normalizeBuildId(record.targetBuildId)
  const attempt =
    typeof record.attempt === 'number' || typeof record.attempt === 'string'
      ? Number(record.attempt)
      : Number.NaN
  const nextRetryAt =
    typeof record.nextRetryAt === 'number' ||
    typeof record.nextRetryAt === 'string'
      ? Number(record.nextRetryAt)
      : Number.NaN
  if (
    !sourceBuildId ||
    !targetBuildId ||
    !Number.isSafeInteger(attempt) ||
    attempt < 1 ||
    !Number.isFinite(nextRetryAt) ||
    nextRetryAt < 0
  ) {
    return null
  }
  return { sourceBuildId, targetBuildId, attempt, nextRetryAt }
}

/**
 * 计算指定刷新次数对应的指数退避时长。
 * @param attempt 当前版本组合的刷新次数，从 1 开始。
 * @param baseDelayMs 基础退避时长。
 * @param maxDelayMs 最大退避时长。
 * @returns 不超过最大值的退避毫秒数。
 */
export const calculateReleaseRetryDelay = (
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number
): number => {
  const normalizedBase = normalizePositiveInteger(baseDelayMs, 1)
  const normalizedMax = Math.max(
    normalizedBase,
    normalizePositiveInteger(maxDelayMs, normalizedBase)
  )
  const exponent = Math.min(Math.max(Math.floor(attempt) - 1, 0), 30)
  return Math.min(normalizedBase * 2 ** exponent, normalizedMax)
}

/**
 * 判断远端构建是否需要使当前页面失效。
 * @param currentBuildId 当前页面构建标识。
 * @param remoteBuildId 服务端最新构建标识。
 * @returns 两个有效标识不一致时返回 true。
 */
export const isReleaseUpdateAvailable = (
  currentBuildId: string,
  remoteBuildId: string | null
): boolean => {
  const current = normalizeBuildId(currentBuildId)
  const remote = normalizeBuildId(remoteBuildId ?? '')
  return Boolean(current && remote && current !== remote)
}

/**
 * 创建保留现有路由、查询参数与锚点的防缓存替换地址。
 * @param currentUrl 当前页面完整地址。
 * @param record 本次刷新重试记录。
 * @param requestedAt 本次替换请求时间戳。
 * @returns 可交给 location.replace 的完整地址。
 */
export const createReleaseReloadUrl = (
  currentUrl: string,
  record: ReleaseRetryRecord,
  requestedAt: number
): string => {
  const url = new URL(currentUrl)
  url.searchParams.set(RELEASE_SOURCE_QUERY_KEY, record.sourceBuildId)
  url.searchParams.set(RELEASE_TARGET_QUERY_KEY, record.targetBuildId)
  url.searchParams.set(RELEASE_ATTEMPT_QUERY_KEY, String(record.attempt))
  url.searchParams.set(RELEASE_RETRY_AT_QUERY_KEY, String(record.nextRetryAt))
  url.searchParams.set(RELEASE_NONCE_QUERY_KEY, String(requestedAt))
  return url.href
}

/**
 * 从防缓存地址解析刷新重试记录。
 * @param currentUrl 携带发布失效参数的页面完整地址。
 * @returns 合法地址参数对应的刷新记录；结构无效时返回 null。
 */
export const parseReleaseRetryRecordFromUrl = (
  currentUrl: string
): ReleaseRetryRecord | null => {
  try {
    const url = new URL(currentUrl)
    return parseReleaseRetryRecord({
      sourceBuildId: url.searchParams.get(RELEASE_SOURCE_QUERY_KEY),
      targetBuildId: url.searchParams.get(RELEASE_TARGET_QUERY_KEY),
      attempt: url.searchParams.get(RELEASE_ATTEMPT_QUERY_KEY),
      nextRetryAt: url.searchParams.get(RELEASE_RETRY_AT_QUERY_KEY)
    })
  } catch {
    return null
  }
}

/**
 * 执行单次旧版本失效检查。
 * @param runtime 当前构建、远端版本、重试状态与页面替换能力。
 * @returns 本次检查是否触发页面替换。
 */
export const invalidateStaleRelease = async (
  runtime: ReleaseInvalidationRuntime
): Promise<boolean> => {
  let remoteBuildId: string | null = null
  try {
    remoteBuildId = await runtime.fetchBuildId()
  } catch {
    return false
  }

  const currentBuildId = normalizeBuildId(runtime.currentBuildId)
  const targetBuildId = normalizeBuildId(remoteBuildId ?? '')
  if (!currentBuildId || !targetBuildId) return false
  if (currentBuildId === targetBuildId) {
    runtime.clearRetryRecord()
    return false
  }

  const now = runtime.now()
  const previousRecord = runtime.readRetryRecord()
  const isSameVersionPair = Boolean(
    previousRecord?.sourceBuildId === currentBuildId &&
      previousRecord.targetBuildId === targetBuildId
  )
  const retryMaxDelayMs = normalizePositiveInteger(
    runtime.retryMaxDelayMs,
    DEFAULT_RELEASE_RETRY_MAX_DELAY_MS
  )
  const remainingRetryDelayMs = previousRecord
    ? previousRecord.nextRetryAt - now
    : 0
  if (
    isSameVersionPair &&
    remainingRetryDelayMs > 0 &&
    remainingRetryDelayMs <= retryMaxDelayMs
  ) {
    return false
  }

  const attempt =
    isSameVersionPair && previousRecord ? previousRecord.attempt + 1 : 1
  const retryDelayMs = calculateReleaseRetryDelay(
    attempt,
    runtime.retryBaseDelayMs,
    retryMaxDelayMs
  )
  const record: ReleaseRetryRecord = {
    sourceBuildId: currentBuildId,
    targetBuildId,
    attempt,
    nextRetryAt: now + retryDelayMs
  }
  runtime.writeRetryRecord(record)

  try {
    runtime.replace(runtime.createReloadUrl(record, now))
    return true
  } catch {
    return false
  }
}

/**
 * 获取当前服务端部署的构建标识。
 * @param currentBuildId 当前页面构建标识，用于生成防缓存查询参数。
 * @returns 最新构建标识；请求失败或清单无效时返回 null。
 */
const fetchRemoteBuildId = async (
  currentBuildId: string
): Promise<string | null> => {
  try {
    const query = new URLSearchParams({
      current: currentBuildId,
      timestamp: String(Date.now())
    })
    const response = await fetch(`${VERSION_MANIFEST_PATH}?${query}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) return null
    return parseBuildVersionManifest(await response.json())
  } catch {
    return null
  }
}

/**
 * 从当前页面地址恢复刷新重试记录。
 * @returns 合法地址参数对应的刷新记录；不存在时返回 null。
 */
const readRetryRecordFromUrl = (): ReleaseRetryRecord | null => {
  return parseReleaseRetryRecordFromUrl(window.location.href)
}

/**
 * 读取浏览器中的刷新重试记录。
 * @returns 会话存储记录；存储不可用时回退地址参数记录。
 */
const readBrowserRetryRecord = (): ReleaseRetryRecord | null => {
  try {
    const storedValue = sessionStorage.getItem(RELEASE_RETRY_STORAGE_KEY)
    if (storedValue) {
      const storedRecord = parseReleaseRetryRecord(JSON.parse(storedValue))
      if (storedRecord) return storedRecord
    }
  } catch {
    // 隐私模式禁用会话存储时，继续从防缓存地址恢复退避状态。
  }
  return readRetryRecordFromUrl()
}

/**
 * 写入浏览器刷新重试记录。
 * @param record 当前版本组合的刷新重试记录。
 */
const writeBrowserRetryRecord = (record: ReleaseRetryRecord): void => {
  try {
    sessionStorage.setItem(RELEASE_RETRY_STORAGE_KEY, JSON.stringify(record))
  } catch {
    // 替换地址同时携带退避状态，可在存储不可用时继续防止刷新风暴。
  }
}

/**
 * 清除浏览器刷新重试记录与内部防缓存参数。
 */
const clearBrowserRetryRecord = (): void => {
  try {
    sessionStorage.removeItem(RELEASE_RETRY_STORAGE_KEY)
  } catch {
    // 会话存储不可用时仍继续清理地址中的内部参数。
  }

  try {
    const url = new URL(window.location.href)
    const originalSearch = url.search
    RELEASE_QUERY_KEYS.forEach((key) => url.searchParams.delete(key))
    if (url.search !== originalSearch) {
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      )
    }
  } catch {
    // 地址清理失败不影响后续版本轮询。
  }
}

/**
 * 启动前端旧版本失效监控。
 * @param options 轮询间隔、当前构建标识与刷新退避配置。
 * @returns 停止轮询并解绑页面可见性监听的方法。
 */
export const startReleaseInvalidationMonitor = (
  options: ReleaseInvalidationOptions = {}
): (() => void) => {
  const currentBuildId = normalizeBuildId(
    options.currentBuildId || import.meta.env.VITE_APP_BUILD_ID || 'local'
  )
  const retryBaseDelayMs = normalizePositiveInteger(
    options.retryBaseDelayMs,
    DEFAULT_RELEASE_RETRY_BASE_DELAY_MS
  )
  const retryMaxDelayMs = Math.max(
    retryBaseDelayMs,
    normalizePositiveInteger(
      options.retryMaxDelayMs,
      DEFAULT_RELEASE_RETRY_MAX_DELAY_MS
    )
  )
  const runtime: ReleaseInvalidationRuntime = {
    currentBuildId,
    fetchBuildId: () => fetchRemoteBuildId(currentBuildId),
    readRetryRecord: readBrowserRetryRecord,
    writeRetryRecord: writeBrowserRetryRecord,
    clearRetryRecord: clearBrowserRetryRecord,
    createReloadUrl: (record, requestedAt) =>
      createReleaseReloadUrl(window.location.href, record, requestedAt),
    replace: (url) => window.location.replace(url),
    now: () => Date.now(),
    retryBaseDelayMs,
    retryMaxDelayMs
  }

  /** 检查远端版本，异常时等待下一轮轮询继续恢复。 */
  const checkRelease = (): void => {
    void invalidateStaleRelease(runtime).catch(() => undefined)
  }

  /** 页面重新可见时立即补做一次版本检查。 */
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') checkRelease()
  }

  checkRelease()
  const intervalId = window.setInterval(
    checkRelease,
    normalizePositiveInteger(
      options.intervalMs,
      DEFAULT_RELEASE_CHECK_INTERVAL_MS
    )
  )
  document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    window.clearInterval(intervalId)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
